import type { IconName } from '@luacentrix/icons';
import type { NavItemRecord } from '@luacentrix/ui';

export interface RouteEntry {
  id: string;
  label: string;
  href: string;
  icon: IconName;
  description: string;
}

export const ROUTES: Array<RouteEntry> = [
  {
    id: 'overview',
    label: 'Overview',
    href: '/',
    icon: 'nav',
    description: 'Live platform status read from the API.',
  },
  {
    id: 'projects',
    label: 'Projects',
    href: '/projects',
    icon: 'folder',
    description: 'Projects owned by this organisation.',
  },
  {
    id: 'scripts',
    label: 'Scripts',
    href: '/scripts',
    icon: 'file',
    description: 'Stored sources, their Roblox place ids and the loader built from them.',
  },
  {
    id: 'obfuscator',
    label: 'Obfuscator',
    href: '/obfuscator',
    icon: 'code',
    description: 'Upload a Lua or Luau source into private storage before a build.',
  },
  {
    id: 'licenses',
    label: 'Licenses',
    href: '/licenses',
    icon: 'key',
    description: 'License records and their server-side status.',
  },
  {
    id: 'security',
    label: 'Security',
    href: '/security',
    icon: 'shield',
    description: 'Restrictions, allowlist and policy read from the security service.',
  },
];

export const PUBLIC_ROUTES: Array<RouteEntry> = [
  {
    id: 'blocked',
    label: 'Access restricted',
    href: '/blocked',
    icon: 'shield',
    description: 'Public restriction notice addressed by a reference id.',
  },
];

export const NAV_GROUPS: Array<{ id: string; label: string; routeIds: Array<string> }> = [
  {
    id: 'workspace',
    label: 'Workspace',
    routeIds: ['overview', 'projects', 'scripts', 'obfuscator', 'licenses', 'security'],
  },
];

const routeById = new Map(ROUTES.map(route => [route.id, route]));

export function routeByIdValue(id: string): RouteEntry | undefined {
  return routeById.get(id);
}

export const NAV_ITEMS: Array<NavItemRecord> = NAV_GROUPS.map(group => ({
  id: group.id,
  label: group.label,
  sections: group.routeIds
    .map(id => routeById.get(id))
    .filter((route): route is RouteEntry => route !== undefined)
    .map(route => ({
      id: route.id,
      label: route.label,
      icon: route.icon,
      href: route.href,
    })),
}));

export function activeRouteId(pathname: string): string {
  const exact = ROUTES.find(route => route.href === pathname);
  if (exact !== undefined) return exact.id;
  const prefix = [...ROUTES]
    .filter(route => route.href !== '/' && pathname.startsWith(route.href))
    .sort((a, b) => b.href.length - a.href.length)[0];
  return prefix?.id ?? ROUTES[0]?.id ?? 'overview';
}

export const COLLAPSE_STORAGE_KEY = 'luacentrix.sidebar.collapsed';

export function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
  } catch {
    return;
  }
}
